import { toJS } from "mobx";
import type { Message } from "../models";

class MessageService {
  public buildMessageTree(source: Message[]): { root: Message; messages: Message[] } {
    const conversation = source.map(msg => toJS(msg)) as Message[];

    //order messages by timestamp
    conversation.sort((a, b) => {
      const aOrder = a.order ?? 9999999;
      const bOrder = b.order ?? 9999999;
      if (aOrder !== bOrder) {
          return aOrder - bOrder;
      }

      let ats = a.role === 'system' ? -1 : a.timestamp || 0;
      let bts = b.role === 'system' ? -1 : b.timestamp || 0;
      return ats - bts;
    });

    // fix parentId for legacy messages
    for(const msg of conversation) {
      msg.children = [];
      if (msg.parentId){
        continue;
      }
      if (msg.role === 'system') {
        continue;
      }

      if (msg.order && msg.role === 'assistant') {
        const toolMessage = conversation.find(m => m.order === msg.order && m.role === 'tool');
        if (toolMessage){
          msg.parentId = toolMessage.id;
          continue;
        }
      }

      let parentMessage = conversation.find(m => msg!.order && m.order === msg!.order - 1);
      if (!parentMessage) {
        parentMessage = conversation.find(m => m.role === 'system');
     }

      if (parentMessage){
        msg.parentId = parentMessage.id;
      }
    }

    // find all child messages
    const rootMessage: Message = { id: 'root', role: 'root', content: '', timestamp: 0, children: [] };
    for (const msg of conversation) {
      const parentMessage = conversation.find(m => m.id === msg.parentId) || rootMessage;
      msg.parent = parentMessage;
      parentMessage.children = parentMessage.children || [];
      parentMessage.children.push(msg);
    }

    return {
      root: rootMessage,
      messages: conversation
    }
  }
 
  public getMessageChain(source: Message[], selectedMessageId: string | null): Message[] {
    const { root: rootMessage, messages: conversation } = this.buildMessageTree(source);

    //find last message by timestamp, not index
    let topThreadTimestamp = this.getMaxTimestamp(rootMessage)
    if (selectedMessageId) {
      const selectedMessage = conversation.find(msg => msg.id === selectedMessageId);
      if (selectedMessage){
        topThreadTimestamp = this.getMaxTimestamp(selectedMessage);
      }
    }

    const lastMessageOfThread = conversation.find(msg => msg.timestamp === topThreadTimestamp) || null;
    let currentMessage = lastMessageOfThread;

    const result: Message[] = [];
    //recursively find last message with content
    const processedIds = new Set<string>();
    while (currentMessage) {
      //add current message to bottom of the array
      result.unshift(currentMessage);
      processedIds.add(currentMessage.id || '');
      const parentMessage = conversation.find(msg => msg.id === currentMessage!.parentId && currentMessage!.parentId);
      currentMessage = parentMessage || null;
      if (processedIds.has(currentMessage?.id || '')) {
        //prevent infinite loop in case of circular references
        break;
      }
    }

    return result;
  }
  
  private getMaxTimestamp(messages: Message, processedIds?: Set<string>): number {
    if (!processedIds) {
      processedIds = new Set<string>();
    }

    if (processedIds.has(messages.id)) {
      return 0;
    }

    let maxChildTimestamp = 0;
    for(const child of messages.children || []) {
      const childMax = this.getMaxTimestamp(child, processedIds);
      maxChildTimestamp = Math.max(maxChildTimestamp, childMax);
    }

    if (maxChildTimestamp){
      return maxChildTimestamp;
    }

    return messages.timestamp || 0;
  }

  public getDescendantIds(message: Message): string[] {
    const collectedIds: string[] = [];
    const processedIds = new Set<string>();
    this._getDescendantsInternal(message, collectedIds, processedIds);
    return collectedIds;
  }

  private _getDescendantsInternal(message: Message, collectedIds: string[], processedIds: Set<string>) {
    if (processedIds.has(message.id)) {
      return;
    }

    processedIds.add(message.id);
    collectedIds.push(message.id);

    for (const child of message.children || []) {
      this._getDescendantsInternal(child, collectedIds, processedIds);
    }
  }
}

export default new MessageService();